import { Container, Graphics, Sprite } from "pixi.js";
import { GRID_UNIT } from "@/lib/game/constants";
import { getHueFilter, getPartTexture } from "@/lib/game/partAssets";
import type { PlacedPart } from "@/lib/game/placements";
import type { PartDefinition, PartVariant } from "@/lib/game/types";
import { attachPartParticleFx, partArtGraphics } from "./PartParticleFx";

export const PART_ART_LABEL = "part-art";

interface PartLayers {
  root: Container;
  mask: Graphics;
  aura: Graphics;
  base: Graphics;
  pattern: Graphics;
  overlay: Graphics;
}

function createPartLayers(): PartLayers {
  const root = new Container();
  root.label = PART_ART_LABEL;
  const mask = new Graphics();
  const aura = new Graphics();
  aura.blendMode = "add";
  const base = new Graphics();
  const pattern = new Graphics();
  const overlay = new Graphics();
  pattern.mask = mask;
  overlay.mask = mask;
  root.addChild(aura, base, pattern, overlay, mask);
  return { root, mask, aura, base, pattern, overlay };
}

function strokeW(w: number) {
  return Math.max(2, w * 0.06);
}

function applyPattern(g: Graphics, w: number, h: number, part: PartDefinition) {
  const pattern = part.style.pattern ?? "solid";
  if (pattern === "solid") return;

  const accent = part.style.accent ?? 0xffffff;
  const hw = w / 2;
  const hh = h / 2;

  if (pattern === "striped" || pattern === "bands") {
    const bands = pattern === "bands" ? 4 : 5;
    for (let i = 0; i < bands; i++) {
      const t = (i + 0.5) / bands;
      const y = -hh + h * t;
      g.rect(-hw + 3, y - h * 0.04, w - 6, h * 0.08).fill({ color: accent, alpha: 0.55 });
    }
  } else if (pattern === "dots") {
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 2; col++) {
        g.circle(-hw * 0.45 + col * hw * 0.9, -hh * 0.55 + row * hh * 0.55, w * 0.07).fill({
          color: accent,
          alpha: 0.65,
        });
      }
    }
  } else if (pattern === "checker") {
    const cell = Math.min(w, h) * 0.22;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        if ((row + col) % 2 === 0) continue;
        g.rect(-hw * 0.65 + col * cell, -hh * 0.55 + row * cell, cell * 0.85, cell * 0.85).fill({
          color: accent,
          alpha: 0.45,
        });
      }
    }
  }
}

const RAINBOW_COLORS = [0xff3b30, 0xff9500, 0xffcc00, 0x34c759, 0x007aff, 0x5856d6, 0xaf52de];

function applyVariantEffects(
  layers: PartLayers,
  w: number,
  h: number,
  variant: PartVariant | undefined,
) {
  if (!variant || variant === "standard") return;

  const aura = layers.aura;
  const overlay = layers.overlay;

  if (variant === "trail") {
    for (let i = 0; i < RAINBOW_COLORS.length; i++) {
      const spread = w * 0.34 + i * 2.5;
      const lift = h * 0.42 + i * 1.5;
      aura.moveTo(-spread, lift)
        .quadraticCurveTo(-spread * 0.25, h * 0.04, spread * 0.12, -h * 0.5 - i * 2)
        .stroke({ width: 5 + i * 0.35, color: RAINBOW_COLORS[i], alpha: 0.8 });
      aura.moveTo(spread, lift)
        .quadraticCurveTo(spread * 0.25, h * 0.04, -spread * 0.12, -h * 0.5 - i * 2)
        .stroke({ width: 5 + i * 0.35, color: RAINBOW_COLORS[RAINBOW_COLORS.length - 1 - i]!, alpha: 0.8 });
    }
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 - Math.PI / 2;
      const dist = w * (0.34 + (i % 3) * 0.08);
      overlay
        .star(Math.cos(a) * dist, Math.sin(a) * dist * 0.5, 5, w * 0.08, w * 0.04)
        .fill({ color: RAINBOW_COLORS[i % RAINBOW_COLORS.length]!, alpha: 0.95 });
    }
    overlay
      .circle(0, h * 0.38, w * 0.12)
      .fill({ color: 0xffffff, alpha: 0.55 });
  }

  if (variant === "golden") {
    aura.circle(0, 0, w * 0.58).fill({ color: 0xfff3b0, alpha: 0.22 });
    aura.circle(0, 0, w * 0.42).fill({ color: 0xf4d03f, alpha: 0.18 });
    overlay
      .moveTo(-w * 0.2, -h * 0.38)
      .lineTo(w * 0.24, h * 0.12)
      .stroke({ width: Math.max(3, w * 0.06), color: 0xffffff, alpha: 0.65 });
    overlay.star(w * 0.18, -h * 0.22, 4, w * 0.09, w * 0.045).fill({ color: 0xfff9e6, alpha: 0.9 });
  }

  if (variant === "shiny") {
    aura.circle(-w * 0.1, -h * 0.08, w * 0.5).fill({ color: 0xffffff, alpha: 0.16 });
    overlay
      .moveTo(-w * 0.18, -h * 0.4)
      .lineTo(w * 0.22, h * 0.08)
      .stroke({ width: Math.max(3, w * 0.055), color: 0xffffff, alpha: 0.85 });
    for (let i = 0; i < 3; i++) {
      overlay
        .circle(-w * 0.12 + i * w * 0.14, -h * 0.18 + i * h * 0.06, w * 0.035)
        .fill({ color: 0xffffff, alpha: 0.75 });
    }
  }

  if (variant === "nova") {
    aura.circle(0, 0, w * 0.62).fill({ color: 0xff9ff3, alpha: 0.2 });
    aura.circle(0, 0, w * 0.38).fill({ color: 0xf368e0, alpha: 0.16 });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      overlay
        .star(Math.cos(a) * w * 0.28, Math.sin(a) * h * 0.22, 4, w * 0.09, w * 0.045)
        .fill({ color: i % 2 === 0 ? 0xff9ff3 : 0xffffff, alpha: 0.92 });
    }
  }

  if (variant === "galaxy") {
    aura.circle(0, 0, w * 0.64).fill({ color: 0x686de0, alpha: 0.18 });
    aura.circle(0, 0, w * 0.4).fill({ color: 0x48dbfb, alpha: 0.14 });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const dist = w * (0.16 + (i % 4) * 0.07);
      overlay
        .star(Math.cos(a) * dist, Math.sin(a) * dist * 0.75, 4, w * 0.05, w * 0.025)
        .fill({ color: i % 2 === 0 ? 0x48dbfb : 0xffffff, alpha: 0.9 });
    }
    overlay
      .circle(w * 0.2, -h * 0.12, w * 0.04)
      .fill({ color: 0xffffff, alpha: 0.85 });
    overlay
      .circle(-w * 0.16, h * 0.08, w * 0.03)
      .fill({ color: 0x48dbfb, alpha: 0.85 });
  }
}

function fillMask(layers: PartLayers, drawPath: (g: Graphics) => void) {
  drawPath(layers.mask);
  layers.mask.fill(0xffffff);
}

function drawCone(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(0, -h / 2).lineTo(w / 2, h / 2).lineTo(-w / 2, h / 2).closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
  if (accent !== undefined) {
    layers.base.circle(0, -h * 0.1, w * 0.12).fill(accent);
    layers.base.star(0, -h * 0.25, 4, w * 0.08, w * 0.04).fill(0xffffff);
  }
}

function drawConeBlunt(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(-w / 2, h / 2)
      .lineTo(-w / 2, -h * 0.15)
      .quadraticCurveTo(0, -h / 2 - h * 0.08, w / 2, -h * 0.15)
      .lineTo(w / 2, h / 2)
      .closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawConePointed(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(0, -h / 2 - h * 0.08)
      .lineTo(w * 0.28, h / 2)
      .lineTo(-w * 0.28, h / 2)
      .closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawDome(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const drawBody = (g: Graphics) => {
    g.roundRect(-w / 2, -h * 0.05, w, h * 0.55, w * 0.15);
    g.arc(0, -h * 0.05, w / 2, Math.PI, 0);
  };
  drawBody(layers.mask);
  layers.mask.fill(0xffffff);
  drawBody(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawOgive(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(-w / 2, h / 2)
      .bezierCurveTo(-w / 2, -h * 0.1, w / 2, -h * 0.1, w / 2, h / 2)
      .lineTo(-w / 2, h / 2)
      .closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawCylinder(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent } = part.style;
  const r = w * 0.2;
  const path = (g: Graphics) => {
    g.roundRect(-w / 2, -h / 2, w, h, r);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
  if (accent !== undefined && (part.style.pattern ?? "solid") === "solid") {
    const stripeH = h / 5;
    for (let i = 0; i < 3; i++) {
      layers.base
        .rect(-w / 2 + 4, -h / 2 + stripeH * (i * 2 + 0.5), w - 8, stripeH * 0.6)
        .fill(accent);
    }
  }
}

function drawCylinderPanel(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawCylinder(layers, w, h, part);
  for (let i = -1; i <= 1; i += 2) {
    layers.overlay
      .moveTo(i * w * 0.22, -h / 2 + 4)
      .lineTo(i * w * 0.22, h / 2 - 4)
      .stroke({ width: 2, color: part.style.stroke, alpha: 0.55 });
  }
}

function drawCylinderTaper(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(-w * 0.42, h / 2)
      .lineTo(-w * 0.28, -h / 2)
      .lineTo(w * 0.28, -h / 2)
      .lineTo(w * 0.42, h / 2)
      .closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawCylinderRibbed(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawCylinder(layers, w, h, part);
  for (let i = 0; i < 4; i++) {
    const y = -h / 2 + (h / 4) * (i + 0.55);
    layers.overlay
      .moveTo(-w / 2 + 2, y)
      .lineTo(w / 2 - 2, y)
      .stroke({ width: 2, color: part.style.stroke, alpha: 0.45 });
  }
}

function drawCylinderBulb(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const drawBody = (g: Graphics) => {
    g.ellipse(0, 0, w * 0.48, h * 0.42);
    g.roundRect(-w * 0.28, h * 0.05, w * 0.56, h * 0.42, w * 0.1);
  };
  drawBody(layers.mask);
  layers.mask.fill(0xffffff);
  drawBody(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawEngine(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xe67e22 } = part.style;
  const bodyPath = (g: Graphics) => {
    g.moveTo(-w / 2, -h / 2)
      .lineTo(w / 2, -h / 2)
      .lineTo(w / 2.5, h / 2)
      .lineTo(-w / 2.5, h / 2)
      .closePath();
  };
  fillMask(layers, bodyPath);
  bodyPath(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  layers.base
    .moveTo(-w / 4, h / 2)
    .lineTo(0, h / 2 + h * 0.35)
    .lineTo(w / 4, h / 2)
    .closePath()
    .fill(accent);
  applyPattern(layers.pattern, w, h, part);
}

function drawEngineBell(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xe67e22 } = part.style;
  const drawBody = (g: Graphics) => {
    g.roundRect(-w * 0.42, -h / 2, w * 0.84, h * 0.55, 4);
    g.moveTo(-w * 0.34, h * 0.05)
      .quadraticCurveTo(0, h / 2 + h * 0.35, w * 0.34, h * 0.05)
      .closePath();
  };
  drawBody(layers.mask);
  layers.mask.fill(0xffffff);
  drawBody(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  layers.base.ellipse(0, h * 0.2, w * 0.22, h * 0.12).fill(accent);
  applyPattern(layers.pattern, w, h, part);
}

function drawEngineDual(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xe67e22 } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w / 2, -h / 2, w, h * 0.55, 4);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  for (const sx of [-w * 0.22, w * 0.22]) {
    layers.base.circle(sx, h * 0.18, w * 0.14).fill(accent).stroke({ width: 2, color: stroke });
    layers.base
      .moveTo(sx, h * 0.3)
      .lineTo(sx, h / 2 + h * 0.22)
      .stroke({ width: 3, color: accent });
  }
  applyPattern(layers.pattern, w, h, part);
}

function drawEngineBox(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xe67e22 } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w / 2, -h / 2, w, h * 0.72, 3);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  layers.base.rect(-w * 0.3, h * 0.12, w * 0.6, h * 0.18).fill(accent);
  layers.base.rect(-w * 0.12, h * 0.3, w * 0.24, h * 0.28).fill(0x2c3e50);
  applyPattern(layers.pattern, w, h, part);
}

function drawEngineRing(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawEngine(layers, w, h, part);
  layers.overlay
    .ellipse(0, h * 0.08, w * 0.38, h * 0.14)
    .stroke({ width: 3, color: part.style.accent ?? 0xf39c12 });
}

function drawFin(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const innerX = w / 2;
  const topY = -h / 2;
  const bottomY = h / 2;
  const tipX = -w / 2;
  const tipY = bottomY - h * 0.1;
  const path = (g: Graphics) => {
    g.moveTo(innerX, topY + h * 0.12).lineTo(tipX, tipY).lineTo(innerX, bottomY).closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawFinPreview(
  layers: PartLayers,
  w: number,
  h: number,
  part: PartDefinition,
  path: (g: Graphics) => void,
) {
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(part.style.fill).stroke({ width: strokeW(w), color: part.style.stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawSymmetricFin(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawFinPreview(layers, w, h, part, (g) => {
    g.moveTo(0, -h / 2).lineTo(w / 2, h / 2).lineTo(-w / 2, h / 2).closePath();
  });
}

function drawFinDeltaPreview(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawFinPreview(layers, w, h, part, (g) => {
    g.moveTo(0, -h * 0.2).lineTo(-w / 2, h / 2).lineTo(w / 2, h / 2).closePath();
  });
}

function drawFinSweptPreview(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawFinPreview(layers, w, h, part, (g) => {
    g.moveTo(0, -h * 0.35)
      .lineTo(-w * 0.58, h * 0.12)
      .lineTo(-w * 0.32, h / 2)
      .lineTo(w * 0.32, h / 2)
      .lineTo(w * 0.58, h * 0.12)
      .closePath();
  });
}

function drawFinCanardPreview(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawFinPreview(layers, w, h, part, (g) => {
    g.moveTo(0, -h * 0.3)
      .lineTo(-w * 0.38, -h * 0.04)
      .lineTo(-w * 0.22, h / 2)
      .lineTo(w * 0.22, h / 2)
      .lineTo(w * 0.38, -h * 0.04)
      .closePath();
  });
}

function drawFinClippedPreview(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  drawFinPreview(layers, w, h, part, (g) => {
    g.moveTo(0, -h * 0.15)
      .lineTo(-w * 0.2, -h * 0.15)
      .lineTo(-w * 0.42, h * 0.22)
      .lineTo(-w * 0.18, h / 2)
      .lineTo(w * 0.18, h / 2)
      .lineTo(w * 0.42, h * 0.22)
      .lineTo(w * 0.2, -h * 0.15)
      .closePath();
  });
}

function drawFinDelta(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(w / 2, -h * 0.2).lineTo(-w * 0.55, h / 2).lineTo(w / 2, h / 2).closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawFinSwept(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(w / 2, -h * 0.35).lineTo(-w * 0.65, h * 0.15).lineTo(w / 2, h / 2).closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawFinCanard(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(w / 2, -h * 0.05)
      .lineTo(-w * 0.15, -h / 2)
      .lineTo(-w * 0.35, h / 2)
      .lineTo(w / 2, h * 0.35)
      .closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawFinClipped(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.moveTo(w / 2, -h * 0.15)
      .lineTo(-w * 0.2, -h * 0.15)
      .lineTo(-w / 2, h * 0.25)
      .lineTo(w / 2, h / 2)
      .closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawCapsule(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w / 2, -h / 2, w, h, w / 2);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  if (accent !== undefined) {
    layers.base.circle(0, -h * 0.25, w * 0.2).fill(accent);
  }
  applyPattern(layers.pattern, w, h, part);
}

function drawBoosterSlim(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w * 0.32, -h / 2, w * 0.64, h, w * 0.32);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawBoosterFat(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w * 0.55, -h / 2, w * 1.1, h, w * 0.35);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawBoosterBottle(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const drawBody = (g: Graphics) => {
    g.roundRect(-w * 0.28, -h / 2, w * 0.56, h * 0.72, w * 0.2);
    g.roundRect(-w * 0.18, -h * 0.62, w * 0.36, h * 0.22, w * 0.12);
  };
  drawBody(layers.mask);
  layers.mask.fill(0xffffff);
  drawBody(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  applyPattern(layers.pattern, w, h, part);
}

function drawBoosterDrum(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w * 0.55, -h / 2, w * 1.1, h, w * 0.25);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: strokeW(w), color: stroke });
  layers.base.ellipse(0, -h / 2 + 3, w * 0.5, h * 0.12).fill({ color: stroke, alpha: 0.35 });
  applyPattern(layers.pattern, w, h, part);
}

function drawPorthole(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xffffff } = part.style;
  const r = Math.min(w, h) * 0.42;
  const path = (g: Graphics) => {
    g.circle(0, 0, r + 4);
    g.circle(0, 0, r);
  };
  path(layers.mask);
  layers.mask.fill(0xffffff);
  layers.base.circle(0, 0, r + 4).fill(stroke);
  layers.base.circle(0, 0, r).fill(fill);
  layers.base.circle(-r * 0.25, -r * 0.25, r * 0.2).fill(accent);
}

function drawWindowHex(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xffffff } = part.style;
  const r = Math.min(w, h) * 0.38;
  const path = (g: Graphics) => {
    g.moveTo(r, 0);
    for (let i = 1; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    g.closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: 3, color: stroke });
  layers.base.circle(-r * 0.2, -r * 0.2, r * 0.18).fill(accent);
}

function drawWindowOval(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xffffff } = part.style;
  const path = (g: Graphics) => {
    g.ellipse(0, 0, w * 0.4, h * 0.34);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: 3, color: stroke });
  layers.base.ellipse(-w * 0.08, -h * 0.08, w * 0.1, h * 0.08).fill(accent);
}

function drawWindowStrip(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xffffff } = part.style;
  const path = (g: Graphics) => {
    g.roundRect(-w * 0.42, -h * 0.28, w * 0.84, h * 0.56, 6);
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: 3, color: stroke });
  layers.base.roundRect(-w * 0.3, -h * 0.12, w * 0.22, h * 0.24, 4).fill(accent);
  layers.base.roundRect(w * 0.08, -h * 0.12, w * 0.22, h * 0.24, 4).fill(accent);
}

function drawWindowDiamond(layers: PartLayers, w: number, h: number, part: PartDefinition) {
  const { fill, stroke, accent = 0xffffff } = part.style;
  const r = Math.min(w, h) * 0.42;
  const path = (g: Graphics) => {
    g.moveTo(0, -r).lineTo(r, 0).lineTo(0, r).lineTo(-r, 0).closePath();
  };
  fillMask(layers, path);
  path(layers.base);
  layers.base.fill(fill).stroke({ width: 3, color: stroke });
  layers.base.circle(-r * 0.15, -r * 0.15, r * 0.15).fill(accent);
}

function drawShape(
  layers: PartLayers,
  part: PartDefinition,
  w: number,
  h: number,
  preview?: boolean,
) {
  switch (part.shape) {
    case "cone":
      drawCone(layers, w, h, part);
      break;
    case "cone-blunt":
      drawConeBlunt(layers, w, h, part);
      break;
    case "cone-pointed":
      drawConePointed(layers, w, h, part);
      break;
    case "dome":
      drawDome(layers, w, h, part);
      break;
    case "ogive":
      drawOgive(layers, w, h, part);
      break;
    case "cylinder":
      drawCylinder(layers, w, h, part);
      break;
    case "cylinder-panel":
      drawCylinderPanel(layers, w, h, part);
      break;
    case "cylinder-taper":
      drawCylinderTaper(layers, w, h, part);
      break;
    case "cylinder-ribbed":
      drawCylinderRibbed(layers, w, h, part);
      break;
    case "cylinder-bulb":
      drawCylinderBulb(layers, w, h, part);
      break;
    case "engine":
      drawEngine(layers, w, h, part);
      break;
    case "engine-bell":
      drawEngineBell(layers, w, h, part);
      break;
    case "engine-dual":
      drawEngineDual(layers, w, h, part);
      break;
    case "engine-box":
      drawEngineBox(layers, w, h, part);
      break;
    case "engine-ring":
      drawEngineRing(layers, w, h, part);
      break;
    case "triangle":
      if (preview) drawSymmetricFin(layers, w, h, part);
      else drawFin(layers, w, h, part);
      break;
    case "fin-delta":
      if (preview) drawFinDeltaPreview(layers, w, h, part);
      else drawFinDelta(layers, w, h, part);
      break;
    case "fin-swept":
      if (preview) drawFinSweptPreview(layers, w, h, part);
      else drawFinSwept(layers, w, h, part);
      break;
    case "fin-canard":
      if (preview) drawFinCanardPreview(layers, w, h, part);
      else drawFinCanard(layers, w, h, part);
      break;
    case "fin-clipped":
      if (preview) drawFinClippedPreview(layers, w, h, part);
      else drawFinClipped(layers, w, h, part);
      break;
    case "capsule":
      drawCapsule(layers, w, h, part);
      break;
    case "booster-slim":
      drawBoosterSlim(layers, w, h, part);
      break;
    case "booster-fat":
      drawBoosterFat(layers, w, h, part);
      break;
    case "booster-bottle":
      drawBoosterBottle(layers, w, h, part);
      break;
    case "booster-drum":
      drawBoosterDrum(layers, w, h, part);
      break;
    case "porthole":
      drawPorthole(layers, w, h, part);
      break;
    case "window-hex":
      drawWindowHex(layers, w, h, part);
      break;
    case "window-oval":
      drawWindowOval(layers, w, h, part);
      break;
    case "window-strip":
      drawWindowStrip(layers, w, h, part);
      break;
    case "window-diamond":
      drawWindowDiamond(layers, w, h, part);
      break;
  }
  applyVariantEffects(layers, w, h, part.style.variant);
}

export function createPartGraphics(
  part: PartDefinition,
  _options?: { preview?: boolean },
): Container {
  const root = new Container();
  root.label = PART_ART_LABEL;

  const w = part.dimensions.gridW * GRID_UNIT;
  const h = part.dimensions.gridH * GRID_UNIT;
  const texture = getPartTexture(part.spriteKey);
  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5);

  if (texture.width > 0 && texture.height > 0) {
    sprite.scale.set(w / texture.width, h / texture.height);
  }

  const hueFilter = getHueFilter(part.hueShift);
  if (hueFilter) sprite.filters = [hueFilter];

  root.addChild(sprite);
  return root;
}

export function createPartNode(part: PartDefinition, options?: { preview?: boolean }): Container {
  const wrap = new Container();
  const art = createPartGraphics(part, options);
  wrap.addChild(art);
  attachPartParticleFx(wrap, part);
  return wrap;
}

export function partPixelSize(part: PartDefinition) {
  return {
    width: part.dimensions.gridW * GRID_UNIT,
    height: part.dimensions.gridH * GRID_UNIT,
  };
}

export function partPreviewMaxDim(part: PartDefinition): number {
  const { width, height } = partPixelSize(part);
  if (part.category === "engine") return Math.max(width, height * 1.35);
  return Math.max(width, height, 1);
}

export function createPartPreview(part: PartDefinition, boxSize: number): Container {
  const wrap = new Container();

  const plate = new Graphics();
  plate.circle(0, 0, boxSize * 0.44).fill({ color: 0x94a3b8, alpha: 0.22 });
  wrap.addChild(plate);

  const node = createPartNode(part, { preview: true });
  const scale = (boxSize * 0.86) / partPreviewMaxDim(part);
  node.scale.set(scale);
  wrap.addChild(node);

  return wrap;
}

export function orientPartForSlot(
  graphics: Container,
  part: PartDefinition,
  slotId: string,
): void {
  if (part.category !== "fin") return;

  if (slotId === "slot-fin-left") {
    graphics.rotation = 0.11;
    graphics.scale.x = -1;
  } else if (slotId === "slot-fin-right") {
    graphics.rotation = -0.11;
    graphics.scale.x = 1;
  }
}

export function applyPartPlacementTransform(
  wrap: Container,
  part: PartDefinition,
  transform: { rotation?: number; scaleX?: number },
): void {
  const art = partArtGraphics(wrap);
  if (!art) return;

  const rotation = transform.rotation ?? 0;
  const scaleX = transform.scaleX ?? 1;

  if (part.category === "fin") {
    wrap.rotation = 0;
    wrap.scale.x = 1;
    art.rotation = rotation;
    art.scale.x = scaleX;
    return;
  }

  art.rotation = 0;
  art.scale.x = 1;
  wrap.rotation = rotation;
  wrap.scale.x = scaleX;
}

export function orientPartForPlacement(
  graphics: Container,
  part: PartDefinition,
  placed: PlacedPart,
): void {
  if (part.category !== "fin") return;
  graphics.rotation = placed.rotation ?? 0;
  graphics.scale.x = placed.scaleX ?? 1;
}
